<template>
  <div>
    <v-stepper-header class="elevation-0">
      <template v-for="section in form.sections">
        <v-stepper-step
          v-if="!section?.IsHideStepperTitle"
          color="accent"
          edit-icon="$complete"
          :editable="currentStep > section.SectionNumber"
          :ripple="false"
          :key="`step-${section.SectionNumber}-header`"
          :step="section.SectionNumber"
          :complete="currentStep > section.SectionNumber"
        >
          {{ section.SectionTitle }}
        </v-stepper-step>
        <v-divider
          :key="section.SectionNumber"
          v-if="!isTerminus(section)"
        ></v-divider>
      </template>
    </v-stepper-header>
    <div
      class="app-container--width-max d2c-quote-container mx-3 mx-md-0"
      v-if="hasQuotes"
    >
      <TheD2CQuote
        :cover="getCover"
        :sections="sections"
        :section="section"
        :insurer="theCheapest"
        @click="customiseQuote"
        @continueWithQuote="handleContinueWithQuote"
        @showD2CPremiumBreakdown="showD2CPremiumBreakdown"
        @showProductTransferDialog="showProductTransferDialog"
        isCheapest
      ></TheD2CQuote>

      <div v-if="morePrices.length > 0" class="view-more-price__wrapper">
        <a
          v-if="isShowingMorePrices"
          @click="isShowingMorePrices = false"
          class="view-link view-less-link"
        >
          <span>View less prices</span>
          <v-icon> mdi-chevron-up </v-icon>
        </a>
        <a
          v-else
          @click="isShowingMorePrices = true"
          class="view-link view-more-link"
        >
          <span>View more prices</span>
          <v-icon> mdi-chevron-down </v-icon>
        </a>
      </div>

      <div
        v-if="morePrices.length > 0 && isShowingMorePrices"
        class="more-quote-container"
      >
        <h2 class="more-quote-heading ma-0 ma-md-4 px-3">More quotes</h2>
        <div class="more-quote__wrapper">
          <TheD2CQuote
            v-for="(price, index) in morePrices"
            :key="index"
            :cover="getCover"
            :sections="sections"
            :section="section"
            :insurer="price"
            @click="customiseQuote"
            @continueWithQuote="handleContinueWithQuote"
            @showD2CPremiumBreakdown="showD2CPremiumBreakdown"
            @showProductTransferDialog="showProductTransferDialog"
          ></TheD2CQuote>
        </div>
      </div>

      <div v-else class="hide-more-quote-space"></div>

      <BaseDialog ref="shareDialog">
        <template v-slot:content="{ data }">
          <DialogContentShareQuotes
            :content="data?.content"
            :quoteId="data?.quoteId"
            :quoteRequestId="data?.quoteRequestId"
            @success="shareQuotesSuccess"
          ></DialogContentShareQuotes>
        </template>
      </BaseDialog>

      <BaseDialog
        ref="D2CDialogContentPremiumBreakdown"
        windowClass="pa-0 pa-xl-4 pb-xl-0"
        headerClass="pa-4 d2c-dialog-header"
        contentClass="d2c-breakdown-content"
        dark2Icon
        :isSelfServe="isSelfServe"
      >
        <template v-slot:content="{ data }">
          <D2CDialogContentPremiumBreakdown
            :breakdownModel="data"
          ></D2CDialogContentPremiumBreakdown>
        </template>
      </BaseDialog>
      <BaseDialog
        ref="endorsementDetails"
        windowClass="pa-0 pb-xl-0"
        headerClass="pa-4 pt-6 pb-3"
        headerDivider
      >
        <template v-slot:content="{ data }">
          <template v-if="data?.Description">
            <div v-html="data?.Description" class="pa-10"></div>
          </template>
        </template>
      </BaseDialog>
      <BaseDialog
        ref="printQuotes"
        headerIcon="mdi-printer"
        headerIconColor="#106175"
        headerDivider
        windowClass="pa-0 pb-xl-0 mb-0"
        headerClass="px-7 pt-6 pb-3"
        contentClass="mb-0"
      >
        <template v-slot:content="{ data }">
          <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
        </template>
      </BaseDialog>
      <v-overlay :value="overlay">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <BaseDialog
        ref="payMonthly"
        windowClass="pa-0 pb-xl-0"
        headerClass="pa-4 pb-5 mb-n1"
        headerDivider
      >
        <template v-slot:content>
          <DialogContentPayMonthly />
        </template>
      </BaseDialog>
    </div>
    <div class="app-container--width-max" v-else>
      <div class="no-quote-container pt-7 mb-10 mx-3 mx-xl-0">
        <div class="px-4 px-xl-10">
          <BaseButton
            text="Back to 'Your cover'"
            flat
            ripple
            class="font-weight-bold d-flex back-btn ml-lg-n9 back-previous__btn-link mt-1"
            icon="mdi-chevron-left"
            color="#106175"
            small
            @click="handleBack({ section })"
          />
          <h2 class="font-24 font-weight-bold mt-5 mb-4 mb-xl-10 pl-xl-5">
            Your quote
          </h2>
        </div>

        <v-divider class="mx-xl-10"></v-divider>
        <div class="text-center info__wrapper pb-0">
          <h3
            class="font-weight-bold mb-9"
            :class="$vuetify.breakpoint.lgAndUp ? 'font-24' : 'font-28'"
          >
            Sorry!
          </h3>
          <p class="font-weight-regular mb-9 px-0 px-lg-10">
            Unfortunately, we were unable to provide you with a quote for the
            cover options you have chosen. Please click the customise button
            below to review your chosen cover levels, specified items and
            optional extras.
          </p>
          <BaseButton
            :text="'CUSTOMISE'"
            height="60"
            :minWidth="$vuetify.breakpoint.lgAndUp ? '520' : '100%'"
            :block="$vuetify.breakpoint.lgAndUp ? false : true"
            :color="'#106175'"
            class="font-weight-bold font-20 rounded-pill px-10 white--text btn-quote"
            @click="customiseQuote"
          />
        </div>
        <div class="px-0 px-lg-10">
          <v-divider class="mt-10 mt-lg-16"></v-divider>
        </div>
        <div class="text-center info__wrapper pt-0">
          <h3
            class="font-weight-bold mb-9 mt-9 mt-lg-14"
            :class="$vuetify.breakpoint.lgAndUp ? 'font-24' : 'font-28'"
          >
            Contact us
          </h3>

          <p class="font-weight-regular mb-9 px-0 px-lg-10">
            Alternatively, you can contact us to speak to a member of our team
            who may be able to assist you. Please call us on the number below.
          </p>
          <h3 class="font-32 text--gray-dark mb-8 font-weight-semi-bold">
            {{ contactInfo?.PhoneNumber }}
          </h3>
          <div>
            <p class="mb-0 font-weight-bold">Opening Hours</p>
            <div
              v-for="(itemTime, index) in contactInfo?.BusinessTimes"
              :key="index"
            >
              <p class="mb-0">
                {{ itemTime?.Description }}:
                {{ itemTime?.OpenTime | timeFormat12h }} -
                {{ itemTime?.CloseTime | timeFormat12h }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <v-navigation-drawer
      ref="customiseDrawer"
      v-model="isShowDrawerCustomiseCover"
      app
      right
      temporary
      class="v-navigation-drawer--width-max drawer-customise-cover__container"
      :stateless="isStateless"
    >
      <CustomiseCover
        ref="customiseDrawerInner"
        :sections="sections"
        :section="section"
        :refreshQuote="true"
        :isDrawer="true"
        @closeDrawer="closeDrawer"
      ></CustomiseCover>
    </v-navigation-drawer>

    <ProductTransferDialog
      ref="productTransfer"
      @setAd="setAd"
      @refreshCover="refreshCover()"
    ></ProductTransferDialog>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";
import { adBoolToString, adStringToBool } from "@/common/cover";
import { EXCLUDED, TOOL_TIP_TEXT } from "@/constants/common.constants.js";
import router from "@/router";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
  STEPS_MODULE,
} from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "QuoteResult",
  components: {
    TheD2CQuote: () => import("@/components/QuoteResult/TheD2CQuote.vue"),
    CustomiseCover: () => import("@/views/StageQuotation/CustomiseCover.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentPayMonthly: () =>
      import("@/components/QuoteResult/DialogContentPayMonthly"),
    D2CDialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/D2CDialogContentPremiumBreakdown.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    ProductTransferDialog: () =>
      import("@/views/Dialog/ProductTransferDialog.vue"),
  },
  mixins: [PremiumBreakdownMixin, BuildingsContentsVisibility],
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    section: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isShowDrawerCustomiseCover: false,
      isShowBadgeDefaqto: true,
      TOOL_TIP_TEXT,
      documentList: [],
      selectedAds: {
        buildingsAd: EXCLUDED,
        contentsAd: EXCLUDED,
      },
      isShowingMorePrices: false,
      selectedQuote: null,
      isStateless: true,
    };
  },
  watch: {
    isShowDrawerCustomiseCover(val) {
      if (!val) {
        this.isShowBadgeDefaqto = true;
      }
    },
    forceRefreshQuote(val) {
      if (val) {
        this.callRefreshQuotes();
      }
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getCover",
      "hasQuotes",
      "getQuoteRequestId",
      "httpConfig",
      "insurerQuotesModel",
      "coverSelection",
      "getAnswer",
      "callChannel",
      "quilterSelectedCoverMatchesFactFind",
      "isSelfServe",
      "contactInfo",
      "replacementProductAvailable",
    ]),
    ...mapState(QUESTIONS_MODULE, ["overlay", "factFind", "needRefreshQuote"]),
    ...mapState(APPLICATION_MODULE, ["form"]),
    theCheapest() {
      return this.insurerQuotesModel.find((quote) => quote.isCheapest);
    },
    morePrices() {
      return this.insurerQuotesModel.filter(
        (quote) => !quote.isCheapest && quote.isAvailable
      );
    },
    secondAppInc() {
      return this.getAnswer("SecondApplicantIncluded");
    },
    forceRefreshQuote() {
      return this.needRefreshQuote;
    },
  },
  mounted() {
    // Restore accidental damage selections
    this.selectedAds.buildingsAd = adBoolToString(
      this.coverSelection.buildingsAd
    );
    this.selectedAds.contentsAd = adBoolToString(
      this.coverSelection.contentsAd
    );
    this.getAppConfigurationContactInfo();
    if (this.needRefreshQuote) {
      this.callRefreshQuotes();
    }
  },
  beforeDestroy() {
    document.getElementsByTagName("html")[0].style.overflow = "";
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "updateCommissionSacrifice",
      "setCoverSelection",
      "setOverlayVisible",
      "saveQuoteResults",
      "getAppConfigurationContactInfo",
      "setRefreshQuote",
    ]),
    ...mapActions(STEPS_MODULE, ["handleGoApplication", "handleBack"]),
    ...mapActions(APPLICATION_MODULE, [
      "setApplicationActiveQuote",
      "updateSelectedQuote",
    ]),
    handleContinueWithQuote(insurer) {
      this.selectedQuote = insurer.selectedQuote;
      if (this.isQuilterFactFind) {
        this.quilterValidation(insurer);
      } else {
        this.proceedWithApply(insurer);
      }
    },
    quilterValidation(insurer) {
      const isSelectedQuoteAffordable = this.quilterIsAffordable(
        insurer.selectedQuote
      );
      if (!isSelectedQuoteAffordable) {
        this.showQuilterAffordabilityWarning();
      } else if (!this.quilterSelectedCoverMatchesFactFind) {
        this.showQuilterCoverWarning();
      } else {
        // Proceed as normal if above validation has passed.
        this.proceedWithApply(insurer);
      }
    },
    quilterIsAffordable(selectedQuote) {
      const annualSpendableIncome = this.factFind.totalSpendableIncome * 12;
      const selectedQuoteAnnualCost = selectedQuote?.AnnualTotal.toFixed(2);
      return annualSpendableIncome > selectedQuoteAnnualCost;
    },
    showQuilterAffordabilityWarning() {
      this.$refs.quilterCoverAffordabilityWarning.open("Affordability check");
    },
    showQuilterCoverWarning() {
      this.$refs.quilterCoverWarning.open("Warning");
    },
    proceedWithApply(insurer) {
      this.handleGoApplication();
      this.setApplicationActiveQuote(insurer);
      this.updateSelectedQuote();
      router.push({
        path: "application",
        query: {
          quoteId: insurer.selectedQuote.QuoteId,
        },
      });
    },
    calculateFlex(val) {
      this.updateCommissionSacrifice(val);
    },
    shareResults() {
      this.$refs.shareDialog.open("Share via email", {
        content: { ...this.getCover, ...this.selectedAds },
        quoteRequestId: this.getQuoteRequestId,
      });
    },
    shareQuote(quoteId) {
      this.$refs.shareDialog.open("Share via email", {
        content: { ...this.getCover, ...this.selectedAds },
        quoteId: quoteId,
      });
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    setAd(data) {
      this.selectedAds.buildingsAd = adBoolToString(data.buildingsAd);
      this.selectedAds.contentsAd = adBoolToString(data.contentsAd);
      this.setCoverSelection({
        buildingsAd: data.buildingsAd,
        contentsAd: data.contentsAd,
      });
    },
    openCustomiseCoverDialog() {
      this.isShowDrawerCustomiseCover = true;
      document.getElementsByTagName("html")[0].style.overflow = "hidden";
    },
    closeDrawer() {
      this.isShowDrawerCustomiseCover = false;
      document.getElementsByTagName("html")[0].style.overflow = "";
    },
    customiseQuote() {
      if (this.replacementProductAvailable) {
        this.showProductTransferDialog();
        return;
      }
      this.openCustomiseCoverDialog();
      this.isShowBadgeDefaqto = false;
    },
    showEndorsementDetails(endorsement) {
      this.$refs.endorsementDetails.open(endorsement.Title, endorsement);
    },
    showPayMonthly() {
      this.$refs.payMonthly.open("More details");
    },
    refreshCover() {
      const buildingsAd = adStringToBool(this.selectedAds.buildingsAd);
      const contentsAd = adStringToBool(this.selectedAds.contentsAd);
      // Update global state
      this.setCoverSelection({ buildingsAd, contentsAd });
      // Update local models
      this.insurerQuotesModel.forEach((insurer) => {
        insurer.contentsAd = contentsAd;
        insurer.buildingsAd = buildingsAd;
        this.setInsurerQuote(insurer);
      });
    },
    setInsurerQuote(insurer) {
      // Update the local insurer model if it exists (updates screen)
      if (insurer.isAvailable && insurer.quotes) {
        insurer.selectedQuote = insurer.quotes.find(
          (q) =>
            q.BuildingsAD === insurer.buildingsAd &&
            q.ContentsAD === insurer.contentsAd
        );
      }
    },
    isTerminus(section) {
      const totalSectionNeedShowStepperTitle = this.form.sections.filter(
        (item) => !item.IsHideStepperTitle
      ).length;
      const isTheLastItem =
        section.SectionNumber >=
        this.form.sections[totalSectionNeedShowStepperTitle - 1].SectionNumber;
      return isTheLastItem;
    },
    callRefreshQuotes() {
      this.setRefreshQuote(false);
      this.openCustomiseCoverDialog();
      setTimeout(() => {
        this.$refs?.customiseDrawerInner?.getQuote();
      }, 100);
    },
    showProductTransferDialog() {
      this.$refs.productTransfer.open();
    },
  },
};
</script>

<style lang="scss" scoped>
.text--gray-dark {
  color: $gray-dark;
}
.d2c-quote-container {
  :deep(.theme--light.v-card) {
    color: #272727;
  }
}
.no-quote-container {
  background: #fff;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  color: $black-2;
  .info__wrapper {
    padding: 74px 160px 76px;
    color: $black-2;
  }
  :deep(.back-btn) {
    .v-icon {
      margin-right: 0 !important;
    }
  }
}

.view-more-price__wrapper {
  text-align: center;
  margin-top: 41px;
  margin-bottom: 33px;
  a {
    gap: 8px;
    cursor: pointer;
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.4px;
    text-align: center;
    color: #106175;
    user-select: none;
    display: inline-flex;
    align-items: center;
    letter-spacing: 0;
    span {
      letter-spacing: 0.4px;
    }
    i {
      color: #106175;
      font-size: 20px;
    }
  }
}

.more-quote__wrapper {
  margin-bottom: 102px;
}

.hide-more-quote-space {
  margin-bottom: 86px;
}

.more-quote-heading {
  font-size: 28px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #272727;
}

.view-less-link {
  margin-bottom: -2px;
}

.view-more-link {
  margin-bottom: 35px;
}

.view-link {
  margin-top: 12px;
}
@media screen and (max-width: ($grid-breakpoints-lg + 1)) {
  .additional-content {
    border-top: 1px solid #ccd6dd;
  }
  .no-quote-container {
    .info__wrapper {
      padding: 74px 40px 96px;
      color: #000;
    }
  }
  .view-more-price__wrapper {
    margin-top: 35px;
    margin-bottom: -44px;
  }

  .more-quote-container {
    margin-top: 75px;
  }

  .view-more-link {
    margin: 0;
  }
}
</style>
